import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PasswordInput } from '@/components/ui/password-input';
import { Upload, CheckCircle, ChevronRight, ChevronLeft, User, Home, Car, Users, FileSignature } from 'lucide-react';
import axios from 'axios';
import AgreementStep from '@/components/tenant/AgreementStep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const optimizeImageUpload = async file => {
  if (!file || !file.type.startsWith('image/') || file.size <= 700 * 1024) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const maxDimension = 1600;
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob || blob.size >= file.size) return file;
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'dokumen';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
};

const steps = [
  { id: 1, label: 'Data Diri', icon: User },
  { id: 2, label: 'Kontak Darurat', icon: Users },
  { id: 3, label: 'Data Kamar', icon: Home },
  { id: 4, label: 'Kendaraan', icon: Car },
  { id: 5, label: 'Perjanjian', icon: FileSignature },
];

export default function TenantRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [agreementRead, setAgreementRead] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementSignature, setAgreementSignature] = useState('');
  const [agreementCoordinates, setAgreementCoordinates] = useState(null);
  const [agreementLocationError, setAgreementLocationError] = useState('');
  const [agreementManualLocation, setAgreementManualLocation] = useState('');
  const [locating, setLocating] = useState(false);

  const [form, setForm] = useState({
    nama: '', tempat_lahir: '', tanggal_lahir: '', phone: '', pekerjaan: '', alamat_pekerjaan: '',
    instagram: false, instagram_username: '', tiktok: false, tiktok_username: '',
    nama_rekening: '', bca: false, bca_number: '', gopay: false, gopay_number: '', dana: false, dana_number: '',
    ktp: null, bukti_pembayaran: null, bukti_deposit: null,
    kontak_nama: '', kontak_telp: '', kontak_hubungan: '',
    cabang: '', nomor_unit: '', tanggal_mulai_sewa: '', jumlah_penghuni: '1',
    penghuni2_nama: '', penghuni2_phone: '', penghuni2_status: '', penghuni2_ktp: null,
    laundry: 'tidak', cleaning: 'tidak',
    jenis_kendaraan: 'tidak ada', nomor_plat: '',
    password: '', confirm_password: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.nama.trim()) return setError('Nama wajib diisi') || false;
      if (!form.phone.trim()) return setError('Nomor HP wajib diisi') || false;
      if (!form.ktp) return setError('Foto KTP wajib diupload') || false;
      if (!form.bukti_pembayaran) return setError('Bukti pembayaran kamar wajib diupload') || false;
      if (!form.password) return setError('Password wajib diisi') || false;
      if (form.password.length < 6) return setError('Password minimal 6 karakter') || false;
      if (form.password !== form.confirm_password) return setError('Konfirmasi password tidak cocok') || false;
    }
    if (step === 3 && form.jumlah_penghuni === '2') {
      if (!form.penghuni2_nama.trim()) return setError('Nama penghuni kedua wajib diisi') || false;
      if (!form.penghuni2_phone.trim()) return setError('No. HP penghuni kedua wajib diisi') || false;
      if (!form.penghuni2_status) return setError('Status penghuni kedua wajib dipilih') || false;
      if (!form.penghuni2_ktp) return setError('Foto KTP penghuni kedua wajib diupload') || false;
    }
    if (step === 5) {
      if (!agreementRead) return setError('Baca dokumen perjanjian sampai halaman terakhir') || false;
      if (!agreementAccepted) return setError('Persetujuan perjanjian wajib dicentang') || false;
      if (!agreementSignature) return setError('Tanda tangan wajib dibuat') || false;
      if (!agreementManualLocation.trim()) return setError('Daerah penandatanganan wajib dicatat') || false;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const requestAgreementLocation = () => {
    setAgreementLocationError('');
    if (!navigator.geolocation) {
      setAgreementLocationError('Perangkat ini tidak mendukung lokasi GPS.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async position => {
        const coordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setAgreementCoordinates(coordinates);
        try {
          const response = await axios.get(`${BACKEND_URL}/api/tenant/reverse-geocode`, {
            params: { lat: coordinates.latitude, lon: coordinates.longitude },
          });
          setAgreementManualLocation(response.data.area || '');
          setAgreementLocationError('');
        } catch {
          setAgreementManualLocation('');
          setAgreementLocationError('Titik GPS didapat, tetapi nama daerah tidak ditemukan.');
        } finally {
          setLocating(false);
        }
      },
      () => {
        setAgreementCoordinates(null);
        setAgreementLocationError('Lokasi GPS tidak dapat diakses.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  const handleAgreementAccepted = checked => {
    setAgreementAccepted(checked);
    if (checked && !agreementCoordinates && !agreementManualLocation.trim()) requestAgreementLocation();
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true); setUploadProgress(0); setError('');
    try {
      const [ktpFile, paymentFile, depositFile, secondTenantKtpFile] = await Promise.all([
        optimizeImageUpload(form.ktp),
        optimizeImageUpload(form.bukti_pembayaran),
        optimizeImageUpload(form.bukti_deposit),
        optimizeImageUpload(form.jumlah_penghuni === '2' ? form.penghuni2_ktp : null),
      ]);
      const fd = new FormData();
      const fields = ['nama','tempat_lahir','tanggal_lahir','phone','pekerjaan','alamat_pekerjaan',
        'kontak_nama','kontak_telp','kontak_hubungan',
        'nomor_unit','tanggal_mulai_sewa','jumlah_penghuni',
        'jenis_kendaraan','nomor_plat','password'];
      fields.forEach(k => fd.append(k, form[k] || ''));
      fd.append('sosmed_instagram', form.instagram ? (form.instagram_username || '') : '');
      fd.append('sosmed_tiktok', form.tiktok ? (form.tiktok_username || '') : '');
      fd.append('nama_rekening', form.nama_rekening || '');
      fd.append('rekening_bca', form.bca ? (form.bca_number || '') : '');
      fd.append('rekening_gopay', form.gopay ? (form.gopay_number || '') : '');
      fd.append('rekening_dana', form.dana ? (form.dana_number || '') : '');
      fd.append('cabang', form.cabang || '');
      fd.append('laundry', form.laundry === 'ya' ? 'true' : 'false');
      fd.append('cleaning', form.cleaning === 'ya' ? 'true' : 'false');
      if (ktpFile) fd.append('ktp', ktpFile);
      if (paymentFile) fd.append('bukti_pembayaran', paymentFile);
      if (depositFile) fd.append('bukti_deposit', depositFile);
      if (form.jumlah_penghuni === '2') {
        fd.append('penghuni2_nama', form.penghuni2_nama || '');
        fd.append('penghuni2_phone', form.penghuni2_phone || '');
        fd.append('penghuni2_status', form.penghuni2_status || '');
        if (secondTenantKtpFile) fd.append('penghuni2_ktp', secondTenantKtpFile);
      }
      fd.append('agreement_accepted', agreementAccepted ? 'true' : 'false');
      fd.append('agreement_signature', agreementSignature);
      fd.append('agreement_location', agreementManualLocation.trim());
      if (agreementCoordinates) {
        fd.append('agreement_latitude', String(agreementCoordinates.latitude));
        fd.append('agreement_longitude', String(agreementCoordinates.longitude));
        fd.append('agreement_accuracy', String(agreementCoordinates.accuracy || ''));
      }

      const res = await axios.post(`${BACKEND_URL}/api/tenant/self-register`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: event => {
          if (event.total) setUploadProgress(Math.min(99, Math.round((event.loaded * 100) / event.total)));
        },
      });
      setUploadProgress(100);
      setDone({ phone: res.data.phone || form.phone, reg_id: res.data.reg_id });
    } catch (e) {
      setError(e.response?.data?.detail || 'Gagal mendaftar, coba lagi');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="min-h-screen bg-gradient-to-br from-isk-teal/10 to-isk-gold/5 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-xl">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-isk-text mb-2">Pendaftaran Berhasil!</h2>
        <p className="text-isk-muted text-sm mb-6">Data Anda sedang diverifikasi oleh admin. Anda akan dapat login setelah akun diaktifkan.</p>
        <div className="p-4 rounded-2xl bg-isk-teal/10 text-left space-y-2 mb-6">
          <p className="text-xs text-isk-muted font-medium">Kredensial Login Anda:</p>
          <p className="text-sm"><span className="text-isk-muted">Username:</span> <span className="font-bold text-isk-text">{done.phone}</span></p>
          <p className="text-sm"><span className="text-isk-muted">Password:</span> <span className="font-bold text-isk-text">Password yang Anda buat</span></p>
          <p className="text-xs text-isk-muted">Kode pendaftaran: <span className="font-mono">{done.reg_id}</span></p>
        </div>
        <Button onClick={() => navigate('/tenant/login')} className="w-full bg-isk-teal text-white rounded-full">
          Ke Halaman Login
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-isk-teal/10 to-isk-gold/5 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-isk-teal px-6 pt-8 pb-6">
          <h1 className="text-white font-bold text-xl">Pendaftaran Tenant</h1>
          <p className="text-white/70 text-sm mt-1">ISK House Property Management</p>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-5">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors
                  ${step === s.id ? 'bg-white text-isk-teal' : step > s.id ? 'bg-white/40 text-white' : 'bg-white/20 text-white/50'}`}>
                  {step > s.id ? '✓' : s.id}
                </div>
                <span className={`text-xs hidden sm:block ${step === s.id ? 'text-white font-medium' : 'text-white/50'}`}>{s.label}</span>
                {i < steps.length - 1 && <div className={`h-0.5 flex-1 rounded ${step > s.id ? 'bg-white/40' : 'bg-white/20'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          {/* Step 1 — Data Diri */}
          {step === 1 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-isk-text">Data Diri</h3>
              <div><Label>Nama Lengkap *</Label><Input value={form.nama} onChange={e => set('nama', e.target.value)} placeholder="Nama sesuai KTP" className="mt-1 rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tempat Lahir</Label><Input value={form.tempat_lahir} onChange={e => set('tempat_lahir', e.target.value)} placeholder="Kota" className="mt-1 rounded-xl" /></div>
                <div><Label>Tanggal Lahir</Label><Input type="date" value={form.tanggal_lahir} onChange={e => set('tanggal_lahir', e.target.value)} className="mt-1 rounded-xl" /></div>
              </div>
              <div><Label>Nomor HP * <span className="text-isk-muted font-normal">(digunakan untuk login)</span></Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="08xxxxxxxxxx" type="tel" className="mt-1 rounded-xl" /></div>
              <div><Label>Pekerjaan</Label><Input value={form.pekerjaan} onChange={e => set('pekerjaan', e.target.value)} placeholder="Karyawan, Mahasiswa, dll" className="mt-1 rounded-xl" /></div>
              <div><Label>Alamat Pekerjaan / Kampus</Label><Input value={form.alamat_pekerjaan} onChange={e => set('alamat_pekerjaan', e.target.value)} placeholder="Alamat tempat kerja atau kampus" className="mt-1 rounded-xl" /></div>
              <div>
                <Label>Media Sosial</Label>
                <div className="mt-1 space-y-2">
                  {[
                    { key: 'instagram', usernameKey: 'instagram_username', label: 'Instagram', placeholder: '@username' },
                    { key: 'tiktok', usernameKey: 'tiktok_username', label: 'TikTok', placeholder: '@username' },
                  ].map(({ key, usernameKey, label, placeholder }) => (
                    <div key={key} className={`rounded-xl border-2 overflow-hidden transition-colors ${form[key] ? 'border-isk-teal' : 'border-gray-200'}`}>
                      <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${form[key] ? 'bg-isk-teal/10' : 'bg-white/60'}`}>
                        <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="accent-isk-teal w-4 h-4" />
                        <span className={`text-sm font-medium ${form[key] ? 'text-isk-teal' : 'text-isk-muted'}`}>{label}</span>
                      </label>
                      {form[key] && (
                        <div className="px-3 pb-3 bg-isk-teal/5">
                          <Input value={form[usernameKey]} onChange={e => set(usernameKey, e.target.value)} placeholder={placeholder} className="rounded-lg text-sm mt-1" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {[
                { key: 'ktp', label: 'Upload KTP *', placeholder: 'Klik untuk upload foto KTP (wajib)' },
                { key: 'bukti_pembayaran', label: 'Bukti Pembayaran Kamar *', placeholder: 'Klik untuk upload bukti pembayaran (wajib)' },
                { key: 'bukti_deposit', label: 'Bukti Deposit', placeholder: 'Klik untuk upload bukti deposit (opsional)' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <label className={`mt-1 flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-xl cursor-pointer hover:bg-isk-teal/5 transition-colors ${form[key] ? 'border-isk-teal bg-isk-teal/5' : 'border-isk-teal/30'}`}>
                    <Upload className={`w-5 h-5 mb-1 ${form[key] ? 'text-isk-teal' : 'text-isk-muted'}`} />
                    <span className={`text-xs ${form[key] ? 'text-isk-teal font-medium' : 'text-isk-muted'}`}>{form[key] ? `✓ ${form[key].name}` : placeholder}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => set(key, e.target.files?.[0] || null)} />
                  </label>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Password *</Label><PasswordInput value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min. 6 karakter" className="mt-1 rounded-xl" /></div>
                <div><Label>Konfirmasi Password *</Label><PasswordInput value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder="Ulangi password" className="mt-1 rounded-xl" /></div>
              </div>
            </div>
          )}

          {/* Step 2 — Kontak Darurat */}
          {step === 2 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-isk-text">Kontak Keluarga (Tidak Serumah)</h3>
              <div><Label>Nama</Label><Input value={form.kontak_nama} onChange={e => set('kontak_nama', e.target.value)} placeholder="Nama lengkap" className="mt-1 rounded-xl" /></div>
              <div><Label>Nomor Telepon</Label><Input value={form.kontak_telp} onChange={e => set('kontak_telp', e.target.value)} placeholder="08xxxxxxxxxx" type="tel" className="mt-1 rounded-xl" /></div>
              <div>
                <Label>Hubungan</Label>
                <Select value={form.kontak_hubungan} onValueChange={v => set('kontak_hubungan', v)}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih hubungan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Orang Tua">Orang Tua</SelectItem>
                    <SelectItem value="Saudara">Saudara</SelectItem>
                    <SelectItem value="Pasangan">Pasangan</SelectItem>
                    <SelectItem value="Teman">Teman</SelectItem>
                    <SelectItem value="Lainnya">Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3 — Data Kamar */}
          {step === 3 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-isk-text">Data Kamar</h3>
              <div><Label>Cabang / Nama Kost</Label><Input value={form.cabang} onChange={e => set('cabang', e.target.value)} placeholder="Contoh: Jatinegara 1, Gedung Kemayoran" className="mt-1 rounded-xl" /></div>
              <div><Label>Nomor Unit</Label><Input value={form.nomor_unit} onChange={e => set('nomor_unit', e.target.value)} placeholder="Contoh: 101" className="mt-1 rounded-xl" /></div>
              <div><Label>Tanggal Mulai Sewa</Label><Input type="date" value={form.tanggal_mulai_sewa} onChange={e => set('tanggal_mulai_sewa', e.target.value)} className="mt-1 rounded-xl" /></div>
              <div>
                <Label>Jumlah Penghuni</Label>
                <Select value={form.jumlah_penghuni} onValueChange={v => set('jumlah_penghuni', v)}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Orang</SelectItem>
                    <SelectItem value="2">2 Orang</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.jumlah_penghuni === '2' && (
                <div className="space-y-3 p-4 rounded-xl border-2 border-isk-teal/30 bg-isk-teal/5">
                  <p className="text-sm font-semibold text-isk-teal">Data Penghuni Kedua</p>
                  <div><Label>Nama Lengkap *</Label><Input value={form.penghuni2_nama} onChange={e => set('penghuni2_nama', e.target.value)} placeholder="Nama sesuai KTP" className="mt-1 rounded-xl" /></div>
                  <div><Label>Nomor HP *</Label><Input value={form.penghuni2_phone} onChange={e => set('penghuni2_phone', e.target.value)} placeholder="08xxxxxxxxxx" type="tel" className="mt-1 rounded-xl" /></div>
                  <div>
                    <Label>Status *</Label>
                    <Select value={form.penghuni2_status} onValueChange={v => set('penghuni2_status', v)}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pasangan">Pasangan</SelectItem>
                        <SelectItem value="Saudara">Saudara</SelectItem>
                        <SelectItem value="Teman">Teman</SelectItem>
                        <SelectItem value="Lainnya">Lainnya</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Upload KTP *</Label>
                    <label className={`mt-1 flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-xl cursor-pointer hover:bg-isk-teal/10 transition-colors ${form.penghuni2_ktp ? 'border-isk-teal bg-isk-teal/10' : 'border-isk-teal/30'}`}>
                      <Upload className={`w-5 h-5 mb-1 ${form.penghuni2_ktp ? 'text-isk-teal' : 'text-isk-muted'}`} />
                      <span className={`text-xs ${form.penghuni2_ktp ? 'text-isk-teal font-medium' : 'text-isk-muted'}`}>{form.penghuni2_ktp ? `✓ ${form.penghuni2_ktp.name}` : 'Klik untuk upload foto KTP'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => set('penghuni2_ktp', e.target.files?.[0] || null)} />
                    </label>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Laundry</Label>
                  <Select value={form.laundry} onValueChange={v => set('laundry', v)}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ya">Ya</SelectItem>
                      <SelectItem value="tidak">Tidak</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cleaning</Label>
                  <Select value={form.cleaning} onValueChange={v => set('cleaning', v)}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ya">Ya</SelectItem>
                      <SelectItem value="tidak">Tidak</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Kendaraan */}
          {step === 4 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-isk-text">Data Kendaraan</h3>
              <div>
                <Label>Jenis Kendaraan</Label>
                <Select value={form.jenis_kendaraan} onValueChange={v => set('jenis_kendaraan', v)}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tidak ada">Tidak Ada</SelectItem>
                    <SelectItem value="motor">Motor</SelectItem>
                    <SelectItem value="mobil">Mobil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.jenis_kendaraan !== 'tidak ada' && (
                <div><Label>Nomor Plat</Label><Input value={form.nomor_plat} onChange={e => set('nomor_plat', e.target.value.toUpperCase())} placeholder="B 1234 ABC" className="mt-1 rounded-xl" /></div>
              )}

              {/* Rekening Pengembalian Deposit */}
              <div className="pt-2">
                <h3 className="font-semibold text-isk-text mb-2">Rekening Pengembalian Deposit</h3>
                <p className="text-xs text-isk-muted mb-3">Untuk pengembalian uang deposit saat keluar</p>
                <div className="space-y-2">
                  <div>
                    <Label>Nama Pemilik Rekening</Label>
                    <Input value={form.nama_rekening} onChange={e => set('nama_rekening', e.target.value)} placeholder="Nama sesuai rekening" className="mt-1 rounded-xl" />
                  </div>
                  {[
                    { key: 'bca', numKey: 'bca_number', label: 'BCA', placeholder: 'Nomor rekening BCA' },
                    { key: 'gopay', numKey: 'gopay_number', label: 'GoPay', placeholder: 'Nomor GoPay' },
                    { key: 'dana', numKey: 'dana_number', label: 'DANA', placeholder: 'Nomor DANA' },
                  ].map(({ key, numKey, label, placeholder }) => (
                    <div key={key} className={`rounded-xl border-2 overflow-hidden ${form[key] ? 'border-isk-teal' : 'border-gray-200'}`}>
                      <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${form[key] ? 'bg-isk-teal/10' : 'bg-white/60'}`}>
                        <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} className="accent-isk-teal w-4 h-4" />
                        <span className={`text-sm font-medium ${form[key] ? 'text-isk-teal' : 'text-isk-muted'}`}>{label}</span>
                      </label>
                      {form[key] && (
                        <div className="px-3 pb-2.5 bg-isk-teal/5">
                          <Input value={form[numKey]} onChange={e => set(numKey, e.target.value)} placeholder={placeholder} className="mt-1 rounded-lg text-sm" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-sm text-isk-muted">
                <p className="font-medium text-isk-text mb-1">Ringkasan Pendaftaran</p>
                <p>Nama: <span className="text-isk-text font-medium">{form.nama}</span></p>
                <p>No. HP: <span className="text-isk-text font-medium">{form.phone}</span></p>
                <p>Cabang: <span className="text-isk-text font-medium">{form.cabang || '-'}</span></p>
                <p>Unit: <span className="text-isk-text font-medium">{form.nomor_unit || '-'}</span></p>
                <p>Mulai sewa: <span className="text-isk-text font-medium">{form.tanggal_mulai_sewa || '-'}</span></p>
                <p>Laundry: <span className="text-isk-text font-medium">{form.laundry}</span> · Cleaning: <span className="text-isk-text font-medium">{form.cleaning}</span></p>
              </div>
            </div>
          )}

          {/* Step 5 — Perjanjian */}
          {step === 5 && (
            <AgreementStep
              readToEnd={agreementRead}
              setReadToEnd={setAgreementRead}
              accepted={agreementAccepted}
              onAccepted={handleAgreementAccepted}
              signature={agreementSignature}
              setSignature={setAgreementSignature}
              coordinates={agreementCoordinates}
              locationError={agreementLocationError}
              manualLocation={agreementManualLocation}
              setManualLocation={setAgreementManualLocation}
              locating={locating}
              requestLocation={requestAgreementLocation}
            />
          )}

        </div>

        {/* Navigation */}
        <div className="px-6 pb-6 flex gap-3">
          {step > 1 ? (
            <Button variant="outline" onClick={back} className="flex-1 rounded-full">
              <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate('/tenant/login')} className="flex-1 rounded-full">
              Sudah punya akun? Login
            </Button>
          )}
          {step < 5 ? (
            <Button onClick={next} className="flex-1 bg-isk-teal text-white rounded-full">
              Lanjut <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !agreementRead || !agreementAccepted || !agreementSignature || !agreementManualLocation.trim()}
              className="flex-1 bg-isk-teal text-white rounded-full"
            >
              {submitting
                ? (uploadProgress > 0 ? `Mengirim ${uploadProgress}%` : 'Menyiapkan file...')
                : 'Daftar Sekarang'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
